import { useCallback, useEffect, useRef } from 'react';
import { debounce } from 'lodash-es';

import { usePanelPreferenceStore } from '@kc-console/context';

import type { MouseEvent as ReactMouseEvent } from 'react';
import type { LayoutPreferenceType, PanelPreferencePosition } from '@kc-console/context';

/**
 * 패널 크기 상수
 */
const PANEL_SIZE = {
  BOTTOM_TYPE_MIN_HEIGHT: 160,
  BOTTOM_TYPE_MAX_HEIGHT_PERCENT: 0.6,
  RIGHT_TYPE_MIN_WIDTH: 595,
  RIGHT_TYPE_MAX_WIDTH_PERCENT: 0.6,
};

/**
 * 패널 최대 크기 계산
 */
const getPanelMaxSize = (position: PanelPreferencePosition): number => {
  return Math.round(
    position === 'bottom'
      ? window.innerHeight * PANEL_SIZE.BOTTOM_TYPE_MAX_HEIGHT_PERCENT
      : window.innerWidth * PANEL_SIZE.RIGHT_TYPE_MAX_WIDTH_PERCENT,
  );
};

/**
 * 패널 최소 크기 조회
 */
const getPanelMinSize = (position: PanelPreferencePosition): number => {
  return position === 'bottom'
    ? PANEL_SIZE.BOTTOM_TYPE_MIN_HEIGHT
    : PANEL_SIZE.RIGHT_TYPE_MIN_WIDTH;
};

interface UseDraggablePanelLayoutV2Params {
  /** 레이아웃 타입 */
  layoutType: LayoutPreferenceType;
  /** 패널 표시 여부 */
  visible: boolean;
}

/**
 * 드래그 가능 패널 레이아웃 훅
 *
 * - usePanelPreferenceStore 기반 설정 관리
 * - visible 상태를 props로 전달받음 (usePanelViewport에서 제공)
 *
 * @example
 * ```tsx
 * const { visible } = usePanelViewport();
 * const { dragBarRef, targetPanelSize, handleMouseDown } = useDraggablePanelLayoutV2({
 *   layoutType: 'list',
 *   visible,
 * });
 * ```
 */
export function useDraggablePanelLayoutV2({
  layoutType,
  visible,
}: UseDraggablePanelLayoutV2Params) {
  // preferences 상태를 직접 구독하여 변경 시 리렌더링 트리거
  const preference = usePanelPreferenceStore(state => state.preferences[layoutType]);
  const updatePreference = usePanelPreferenceStore(state => state.updatePreference);

  const { position, size } = preference;

  const dragBarRef = useRef<HTMLDivElement>(null);
  const mousemoveAnimationFrame = useRef<number | null>(null);
  const mouseupAnimationFrame = useRef<number | null>(null);
  const startClientPosition = useRef(0);
  const startSize = useRef(0);

  // 드래그를 통해 패널을 조절할 때 이벤트로 전달받은 값을 기준으로 패널의 크기를 계산
  const correctionSize = useCallback(
    (e: MouseEvent) => {
      const targetClientPosition = position === 'bottom' ? e.clientY : e.clientX;
      return startSize.current - (targetClientPosition - startClientPosition.current);
    },
    [position],
  );

  // 브라우저 창 크기 변경 시 패널 크기 조정
  const updatePanelSizeForBrowser = useCallback(() => {
    const maxSize = getPanelMaxSize(position);
    if (maxSize < size) {
      updatePreference(layoutType, { size: maxSize });
    }
  }, [layoutType, position, size, updatePreference]);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (mousemoveAnimationFrame.current) return;

      mousemoveAnimationFrame.current = requestAnimationFrame(() => {
        if (dragBarRef.current) {
          const newSize = correctionSize(e);
          const maxSize = getPanelMaxSize(position);
          const minSize = getPanelMinSize(position);
          const clampedSize = Math.max(Math.min(maxSize, newSize), minSize);

          if (position === 'bottom') {
            dragBarRef.current.style.height = `${clampedSize}px`;
          } else {
            dragBarRef.current.style.width = `${clampedSize}px`;
          }
        }
        mousemoveAnimationFrame.current = null;
      });
    },
    [correctionSize, position],
  );

  const handleMouseUp = useCallback(() => {
    if (mouseupAnimationFrame.current) return;

    mouseupAnimationFrame.current = requestAnimationFrame(() => {
      if (dragBarRef.current) {
        const newSize =
          position === 'bottom' ? dragBarRef.current.clientHeight : dragBarRef.current.clientWidth;

        updatePreference(layoutType, { size: newSize });

        // 드래그를 끝내게 되면 ref로 동적 적용한 style 값은 초기화
        dragBarRef.current.style.cssText = '';
      }
      mouseupAnimationFrame.current = null;
    });

    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove, layoutType, position, updatePreference]);

  const handleMouseDown = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault();

      if (dragBarRef.current) {
        if (position === 'bottom') {
          startClientPosition.current = e.clientY;
          startSize.current = dragBarRef.current.clientHeight;
        } else {
          startClientPosition.current = e.clientX;
          startSize.current = dragBarRef.current.clientWidth;
        }
        // 마우스 드래그 상태에서 조절할 때 움직이는 만큼 변화를 자연스럽게 표시하기 위해 애니메이션 스타일 잠시 비활성화
        dragBarRef.current.style.transition = 'width 0s, height 0s, margin-top 0s';
      }

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [handleMouseMove, handleMouseUp, position],
  );

  useEffect(() => {
    const handleResize = debounce(() => {
      updatePanelSizeForBrowser();
    }, 100);

    if (visible) {
      updatePanelSizeForBrowser();
      window.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updatePanelSizeForBrowser, visible]);

  return {
    /** 드래그 바 ref */
    dragBarRef,
    /** 현재 패널 크기 (px) */
    targetPanelSize: size,
    /** 마우스 다운 핸들러 */
    handleMouseDown,
    /** 패널 위치 */
    position,
  };
}
